import React, { useEffect, useState } from "react";
import { XIcon, AppleLogoIcon } from "@phosphor-icons/react";
import GooglePlayColorIcon from "@/components/GooglePlayColorIcon";

const DISMISS_KEY = "bidorah_get_the_app_dismissed_until";
const RE_PROMPT_DAYS = 7;

// Feature flags — flip to "true" in /app/frontend/.env once the respective
// store listing is live, without changing any component code.
const IOS_ENABLED = process.env.REACT_APP_IOS_ENABLED === "true";
const ANDROID_ENABLED = process.env.REACT_APP_ANDROID_ENABLED !== "false";

// Placeholder store URLs — swap once the apps publish.
// App Store URL format: https://apps.apple.com/app/bidorah/id<NUMERIC_APP_STORE_ID>
// Play Store URL format: https://play.google.com/store/apps/details?id=com.bidorah.app
const APP_STORE_URL =
  process.env.REACT_APP_APP_STORE_URL ||
  "https://apps.apple.com/app/bidorah/id0000000000";
const PLAY_STORE_URL =
  process.env.REACT_APP_PLAY_STORE_URL ||
  "https://play.google.com/store/apps/details?id=com.bidorah.app";

function detectPlatform() {
  if (typeof window === "undefined") return "web";
  const ua = navigator.userAgent || "";
  // Running inside our own Capacitor shell? Don't prompt — they already have us.
  if (window.Capacitor && window.Capacitor.isNativePlatform?.()) return "native";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/android/i.test(ua)) return "android";
  return "web";
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function GetTheAppBanner() {
  const [platform, setPlatform] = useState("web");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);

    // Skip if native, PWA-installed, or desktop web (banner is mobile-only).
    if (p === "native" || p === "web" || isStandalone()) return;

    // Per-platform feature flag — iOS banner stays hidden until the listing is live.
    // iOS users still get the PWA "Add to Home Screen" via InstallAppPrompt.
    if (p === "ios" && !IOS_ENABLED) return;
    if (p === "android" && !ANDROID_ENABLED) return;

    // Respect dismissal cooldown.
    const dismissedUntil = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissedUntil && dismissedUntil > Date.now()) return;

    // Delay 2s so it doesn't fight the first paint.
    const t = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = (e) => {
    e?.stopPropagation();
    const until = Date.now() + RE_PROMPT_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
    setVisible(false);
  };

  if (!visible) return null;

  const isIOS = platform === "ios";
  const storeUrl = isIOS ? APP_STORE_URL : PLAY_STORE_URL;
  const storeLabel = isIOS ? "App Store" : "Play Store";

  // The Apple logo wants a teal-on-white surface to match the original design
  // pattern; the new colorful Google Play icon paints itself, so its surface
  // is plain white instead of teal.
  const iconSurface = isIOS ? "bg-[#1D78AB]" : "bg-white";

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-zinc-900 text-white border-t border-zinc-800 px-4 py-3 flex items-center gap-3 shadow-2xl md:hidden animate-in slide-in-from-bottom-6"
      data-testid="get-the-app-banner"
    >
      <div className={`h-10 w-10 rounded-xl ${iconSurface} grid place-items-center shrink-0`}>
        {isIOS ? (
          <AppleLogoIcon size={22} weight="fill" />
        ) : (
          <GooglePlayColorIcon size={24} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm">Get the Bidorah app</div>
        <div className="text-[11px] text-zinc-400 leading-tight">
          Faster bids, push alerts, no browser bar.
        </div>
      </div>
      <a
        href={storeUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="px-3.5 py-1.5 rounded-full bg-[#1D78AB] hover:bg-[#165d85] text-white text-xs font-bold whitespace-nowrap shrink-0"
        data-testid="get-the-app-cta"
        onClick={() => {
          // Still dismiss so we don't nag after they tap through.
          const until = Date.now() + RE_PROMPT_DAYS * 24 * 60 * 60 * 1000;
          localStorage.setItem(DISMISS_KEY, String(until));
        }}
      >
        {storeLabel}
      </a>
      <button
        onClick={dismiss}
        className="text-zinc-500 hover:text-zinc-300 p-1 shrink-0"
        aria-label="Dismiss"
        data-testid="get-the-app-dismiss"
      >
        <XIcon size={16} />
      </button>
    </div>
  );
}
